/**
 * Map Audit Orchestrator — pełny 5-warstwowy audyt mapy v2.
 *
 * Sekwencja: advisory lock → postValidate (L1) → auditStructural (L2-det)
 *         → auditSemantic (L3) → auditMapCoverage (L5) → (opcjonalnie) callLlmScnAuditV2 (L2-LLM)
 *         → compose audit_report JSONB v2 → UPDATE thematic_maps.
 *
 * Advisory lock: pg_try_advisory_xact_lock(hashtext(mapId::text)) — debounce
 * 5 równoległych auto-run triggerów (useMapGenerator / matcher / senuto /
 * decisionEngine / manual UI). Jeśli zajęty → log + return early.
 *
 * L2 LLM: skip domyślnie (oszczędność Opus). Włącz przez options.skipLLM=false.
 */

import { supabase } from "@/integrations/supabase/client";
import { postValidate } from "./postValidator";
import { auditStructural } from "./auditStructural";
import { auditSemantic } from "./auditSemantic";
import { auditMapCoverage } from "./auditMapCoverage";
import { callLlmScnAuditV2 } from "./llmScnAuditV2";
import { auditLog } from "@/services/contentEngine/contentLogger";
const logPublic = auditLog.public;
const logDebug = auditLog.debug;
const logError = auditLog.error;
import type { AuditConfig, AuditReportV2, AuditVerdict } from "@/types/thematicMaps";

const PREFIX = "[MAP-AUDIT-ORCH]";

export interface AuditMapFullOptions {
  skipLLM?: boolean;
  onLog?: (
    type: "info" | "success" | "error" | "stage",
    message: string,
    debug?: Record<string, unknown>,
  ) => void;
  // Override audit_config z DB
  configOverride?: AuditConfig;
}

export interface AuditMapFullResult {
  success: boolean;
  mapId: string;
  skipped_by_lock?: boolean;
  report?: AuditReportV2;
  error?: string;
}

/**
 * Zsumuj scores 4 core warstw (L2_det + L2_llm?/L3/L5) do total_score 0-100.
 * L1 (postValidate) nie ma punktacji — to tylko stats.
 */
function composeTotalScore(report: Partial<AuditReportV2>): { total: number; verdict: AuditVerdict } {
  const layers = report.layers ?? {};
  const l2d = (layers.L2_deterministic as { score?: number } | undefined)?.score ?? 0;
  const l2l = (layers.L2_llm as { score?: number } | undefined)?.score ?? 0;
  const l3 = (layers.L3 as { score?: number } | undefined)?.score ?? 0;
  const l5 = (layers.L5_coverage as { score?: number } | undefined)?.score ?? 0;
  // L2 LLM: jeśli skipped → ignore, inaczej liczymy do 80
  const llmIncluded = !!layers.L2_llm && !(layers.L2_llm as { skipped?: boolean }).skipped;
  const total = llmIncluded ? l2d + l2l + l3 + l5 : Math.round(((l2d + l3 + l5) / 60) * 100);
  const clamped = Math.max(0, Math.min(100, total));
  const verdict: AuditVerdict = clamped >= 80 ? "pass" : clamped >= 55 ? "warn" : "fail";
  return { total: clamped, verdict };
}

/**
 * Główna funkcja orchestratora. Wykonuje pełny audyt + zapisuje do DB.
 */
export async function auditMapFull(
  mapId: string,
  options: AuditMapFullOptions = {},
): Promise<AuditMapFullResult> {
  const t0 = Date.now();
  const skipLLM = options.skipLLM ?? true; // default: bez LLM (oszczędność)

  // ─── Advisory lock ───
  // Postgres pg_try_advisory_xact_lock — lock zwalnia się automatycznie przy COMMIT.
  // Używamy hashtext(mapId) jako klucz (int4).
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: lockRow, error: lockErr } = await (supabase as any).rpc("try_advisory_audit_lock", {
      p_map_id: mapId,
    });

    // RPC try_advisory_audit_lock może nie istnieć — fallback: proceed bez lock
    // W fazie prod dodamy function w migracji. Dla MVP: log + continue.
    if (lockErr) {
      logDebug(PREFIX, `Advisory lock RPC not available (${lockErr.message}) — proceeding without lock`);
    } else if (lockRow === false) {
      logPublic(PREFIX, `Audit skipped — lock held for mapId=${mapId}`);
      options.onLog?.("info", `ℹ Audyt pominięty — inny audyt już trwa dla tej mapy`);
      return {
        success: true,
        mapId,
        skipped_by_lock: true,
      };
    }
  } catch (e) {
    logDebug(PREFIX, `Lock check error, proceeding: ${e instanceof Error ? e.message : String(e)}`);
  }

  logPublic(PREFIX, `Start auditMapFull for mapId=${mapId} (skipLLM=${skipLLM})`);
  options.onLog?.("info", `▶ Map Audit v2 start (skipLLM=${skipLLM})`);

  // Load audit_config from map
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: mapRow } = await (supabase as any)
    .from("thematic_maps")
    .select("id, website_id, audit_config, audit_report")
    .eq("id", mapId)
    .maybeSingle();

  const config: AuditConfig =
    options.configOverride ??
    ((mapRow as { audit_config?: AuditConfig } | null)?.audit_config ?? {});
  const websiteId = (mapRow as { website_id: string | null } | null)?.website_id ?? null;
  const existingReport = (mapRow as { audit_report?: Record<string, unknown> } | null)?.audit_report ?? {};
  const existingLayers = (existingReport as { layers?: AuditReportV2["layers"] }).layers ?? {};

  // Preserve existing L2_llm gdy skipLLM=true (debugowanie — user wola nie platac za LLM co klik)
  const layers: AuditReportV2["layers"] = skipLLM && existingLayers.L2_llm
    ? { L2_llm: existingLayers.L2_llm }
    : {};
  const issuesCollector: Array<{ layer: string; issue: unknown }> = [];

  // ─── L1 Post-Validate ───
  try {
    options.onLog?.("info", "  L1 postValidate…");
    const l1 = await postValidate(mapId);
    layers.L1 = l1;
  } catch (err) {
    logError(PREFIX, `L1 failed: ${err instanceof Error ? err.message : String(err)}`);
    options.onLog?.("error", `  L1 failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  // ─── L2 Deterministic ───
  try {
    options.onLog?.("info", "  L2 structural (deterministic)…");
    const l2d = await auditStructural(mapId, config);
    layers.L2_deterministic = l2d as unknown as Record<string, unknown>;
    for (const iss of l2d.issues) issuesCollector.push({ layer: "L2_deterministic", issue: iss });
  } catch (err) {
    logError(PREFIX, `L2 deterministic failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  // ─── L3 Semantic ───
  try {
    options.onLog?.("info", "  L3 semantic…");
    const l3 = await auditSemantic(mapId, config);
    layers.L3 = l3 as unknown as Record<string, unknown>;
    for (const iss of l3.issues) issuesCollector.push({ layer: "L3", issue: iss });
  } catch (err) {
    logError(PREFIX, `L3 failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  // ─── L5 Coverage ───
  try {
    options.onLog?.("info", "  L5 map coverage (wp_posts cross-ref)…");
    const l5 = await auditMapCoverage(mapId, websiteId, config);
    layers.L5_coverage = l5 as unknown as Record<string, unknown>;
    for (const iss of l5.issues) issuesCollector.push({ layer: "L5_coverage", issue: iss });
  } catch (err) {
    logError(PREFIX, `L5 failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  // ─── L2 LLM (opcjonalne) ───
  let llmCost = 0;
  if (!skipLLM) {
    try {
      options.onLog?.("info", "  L2 LLM (Opus 4.6, per-seed chunked)…");
      const l2l = await callLlmScnAuditV2(mapId, { onLog: options.onLog });
      layers.L2_llm = l2l as unknown as Record<string, unknown>;
      llmCost = l2l.cost_usd;
    } catch (err) {
      logError(PREFIX, `L2 LLM failed: ${err instanceof Error ? err.message : String(err)}`);
      options.onLog?.("error", `  L2 LLM failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  // ─── Compose total score + verdict ───
  const report: AuditReportV2 = {
    meta: {
      audit_version: "v2",
      audited_at: new Date().toISOString(),
      config_snapshot: config,
      total_score: 0,
      max_score: skipLLM ? 60 : 80,
      overall_verdict: "pass",
      llm_used: !skipLLM && !!layers.L2_llm,
      llm_cost_usd: llmCost,
      duration_ms: Date.now() - t0,
    },
    layers,
    legacy_scn_audit: (existingReport as { dimensions?: unknown })?.dimensions
      ? (existingReport as Record<string, unknown>)
      : undefined,
  };

  const { total, verdict } = composeTotalScore(report);
  report.meta.total_score = total;
  report.meta.max_score = 100;
  report.meta.overall_verdict = verdict;

  // ─── UPDATE thematic_maps ───
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: updErr } = await (supabase as any)
      .from("thematic_maps")
      .update({
        audit_report: report,
        audit_score: total,
        audited_at: report.meta.audited_at,
      })
      .eq("id", mapId);
    if (updErr) throw updErr;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    logError(PREFIX, `UPDATE audit_report failed: ${msg}`);
    return { success: false, mapId, error: msg };
  }

  logPublic(
    PREFIX,
    `Done auditMapFull ${report.meta.duration_ms}ms: score=${total}/100 verdict=${verdict} llm=${report.meta.llm_used}`,
  );
  options.onLog?.(
    "stage",
    `✓ Map Audit v2 done ${(report.meta.duration_ms / 1000).toFixed(1)}s: score=${total}/100 (${verdict})${report.meta.llm_used ? ` · LLM $${llmCost.toFixed(4)}` : ""} · issues=${issuesCollector.length}`,
    { stats: report.meta },
  );

  return { success: true, mapId, report };
}

/**
 * Partial recompute — tylko L5 coverage (tanie, używane po sync wp_posts).
 */
export async function auditMapCoverageOnly(
  mapId: string,
  options: { onLog?: AuditMapFullOptions["onLog"] } = {},
): Promise<AuditMapFullResult> {
  const t0 = Date.now();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: mapRow } = await (supabase as any)
    .from("thematic_maps")
    .select("id, website_id, audit_config, audit_report, audit_score, audited_at")
    .eq("id", mapId)
    .maybeSingle();
  if (!mapRow) return { success: false, mapId, error: "Map not found" };

  const config: AuditConfig = (mapRow as { audit_config?: AuditConfig }).audit_config ?? {};
  const websiteId = (mapRow as { website_id: string | null }).website_id;

  try {
    const l5 = await auditMapCoverage(mapId, websiteId, config);
    // Merge do istniejącego audit_report
    const existing = ((mapRow as { audit_report?: AuditReportV2 }).audit_report ?? {}) as AuditReportV2;
    const layers = { ...(existing.layers ?? {}), L5_coverage: l5 as unknown as Record<string, unknown> };

    const merged: AuditReportV2 = {
      ...existing,
      meta: {
        ...(existing.meta ?? {
          audit_version: "v2",
          audited_at: new Date().toISOString(),
          config_snapshot: config,
          total_score: 0,
          max_score: 100,
          overall_verdict: "pass",
          llm_used: false,
          llm_cost_usd: 0,
          duration_ms: 0,
        }),
        audited_at: new Date().toISOString(),
        duration_ms: Date.now() - t0,
      },
      layers,
    };
    const { total, verdict } = composeTotalScore(merged);
    merged.meta.total_score = total;
    merged.meta.overall_verdict = verdict;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (supabase as any)
      .from("thematic_maps")
      .update({ audit_report: merged, audit_score: total, audited_at: merged.meta.audited_at })
      .eq("id", mapId);

    options.onLog?.(
      "stage",
      `✓ L5 coverage recompute done ${(merged.meta.duration_ms / 1000).toFixed(1)}s`,
      { stats: merged.meta },
    );

    return { success: true, mapId, report: merged };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    logError(PREFIX, `auditMapCoverageOnly failed: ${msg}`);
    return { success: false, mapId, error: msg };
  }
}
